import React, { useEffect } from "react";
import styles from "./BillDetails.module.css";

import { useParams, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Top from "../../components/MainContent/Header/Top/Top";
import SideBanner from "../../components/SideBanner/SideBanner";
import Footer from "../../components/MainContent/Body/Footer/Footer";
import Bill from "../../components/MainContent/Body/BillList/Bill/Bill";

import { AppDispatch } from "../../app/store";
import {
  clearSelectedBill,
  getBill,
  getMyBill,
  payBill,
  selectBill,
  selectBillError,
  selectBillLoading,
} from "../../slices/bill/billSlice";
import { COMPANY } from "../../config/company";

interface BillDetailsProps {
  isAdmin?: boolean;
}

const BillDetails: React.FC<BillDetailsProps> = ({
  isAdmin = false,
}) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const bill = useSelector(selectBill);
  const loading = useSelector(selectBillLoading);
  const error = useSelector(selectBillError);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (isAdmin) {
      dispatch(getBill(id));
    } else {
      dispatch(getMyBill(id));
    }

    return () => {
      dispatch(clearSelectedBill());
    };
  }, [dispatch, id, isAdmin]);

  const handlePayBill = async () => {
    if (!id || !bill) {
      return;
    }

    const confirmed = window.confirm(
      "Czy na pewno chcesz oznaczyć ten rachunek jako opłacony?"
    );

    if (!confirmed) {
      return;
    }

    try {
      await dispatch(payBill(id)).unwrap();
      await dispatch(getBill(id)).unwrap();
    } catch (err) {
      console.error(
        "Nie udało się oznaczyć rachunku jako opłacony:",
        err
      );
    }
  };

  const backPath = isAdmin
    ? "/admin/bills"
    : "/my-bills";

  return (
    <div className={styles.Page}>
      <SideBanner side="left" />

      <div className={styles.Content}>
        <div className={styles.TopAndBorder}>
          <Top />
          <div className={styles.Border}></div>
        </div>

        <div className={styles.PageAndFooter}>
          <div className={styles.Container}>
            <div className={styles.HeaderRow}>
              <h1 className={styles.Title}>
                Szczegóły rachunku
              </h1>

              <NavLink
                to={backPath}
                className={styles.BackLink}
              >
                Wróć do rachunków
              </NavLink>
            </div>

            {loading && !bill && (
              <div className={styles.InfoBox}>
                Ładowanie rachunku...
              </div>
            )}

            {!loading && error && (
              <div className={styles.ErrorBox}>
                {error}
              </div>
            )}

            {!loading && !error && !bill && (
              <div className={styles.InfoBox}>
                Nie znaleziono rachunku.
              </div>
            )}

            {!error && bill && (
              <>
                <Bill
                  title={bill.title}
                  amount={bill.amount}
                  grossAmount={bill.grossAmount}
                  status={bill.status}
                  transferTitle={bill.transferTitle}
                  transferRecipient={COMPANY.companyName}
                  transferAccount={COMPANY.accountNumber}
                  createdAt={bill.createdAt}
                  paidAt={bill.paidAt}
                  billType={bill.type}
                />

                {isAdmin && bill.status === "pending" && (
                  <div className={styles.AdminActions}>
                    <button
                      type="button"
                      className={styles.PayButton}
                      onClick={handlePayBill}
                      disabled={loading}
                    >
                      {loading
                        ? "Zapisywanie..."
                        : "Oznacz jako opłacony"}
                    </button>
                  </div>
                )}

                {isAdmin && bill.status === "paid" && (
                  <div className={styles.PaidInfo}>
                    Rachunek został już oznaczony jako
                    opłacony.
                  </div>
                )}
              </>
            )}
          </div>

          <Footer />
        </div>
      </div>

      <SideBanner side="right" />
    </div>
  );
};

export default BillDetails;